<template>
  <div class="economics-form">
    <a-alert
      type="info"
      show-icon
      class="mb-3"
      message="Plant economics"
      description="These plant-level assumptions drive the per-occurrence cost of every failure mode. Individual steps can override BOM cost, rework minutes, and warranty cost on the next tab."
    />

    <a-row :gutter="[24, 16]">
      <a-col
        v-for="field in ECONOMICS_FIELDS"
        :key="field.key"
        :xs="24"
        :md="12"
      >
        <div class="field-row">
          <div class="field-header">
            <label :for="`vs-econ-${field.key}`" class="field-label">
              {{ field.label }}
              <a-tooltip :title="field.help" placement="top">
                <question-circle-outlined class="ml-1 text-secondary" />
              </a-tooltip>
            </label>
            <a-tag class="field-unit">{{ field.unit }}</a-tag>
          </div>
          <div class="field-controls">
            <a-slider
              :value="Number(localEconomics[field.key] ?? 0)"
              :min="field.min"
              :max="field.max"
              :step="field.step"
              class="field-slider"
              @change="(val) => handleChange(field.key, val)"
            />
            <a-input-number
              :id="`vs-econ-${field.key}`"
              :value="Number(localEconomics[field.key] ?? 0)"
              :min="field.min"
              :max="field.max"
              :step="field.step"
              class="field-number"
              @change="(val) => handleChange(field.key, val)"
            />
          </div>
        </div>
      </a-col>
    </a-row>

    <a-divider class="my-3">Preview: per-occurrence cost by failure mode</a-divider>
    <div class="preview-strip">
      <a-tag
        v-for="mode in FAILURE_MODES"
        :key="mode.id"
        :style="{
          background: mode.bg,
          color: mode.fg,
          border: 'none',
          padding: '4px 10px',
          fontSize: '13px',
          fontWeight: 500,
        }"
      >
        {{ mode.short }}
        <span class="preview-cost">
          {{ formatCurrency(previewCost(mode.id)) }}
        </span>
      </a-tag>
    </div>
  </div>
</template>

<script>
import { QuestionCircleOutlined } from '@ant-design/icons-vue';
import {
  DEFAULT_ECONOMICS,
  ECONOMICS_FIELDS,
  FAILURE_MODES,
} from 'src/utils/valueStory/config';
import { costPerOccurrence } from 'src/utils/valueStory/cost';

export default {
  components: { QuestionCircleOutlined },
  props: {
    economics: { type: Object, default: () => ({ ...DEFAULT_ECONOMICS }) },
  },
  emits: ['update:economics'],
  setup() {
    return { ECONOMICS_FIELDS, FAILURE_MODES };
  },
  computed: {
    localEconomics() {
      return { ...DEFAULT_ECONOMICS, ...(this.economics || {}) };
    },
  },
  methods: {
    handleChange(key, value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return;
      this.$emit('update:economics', {
        ...this.localEconomics,
        [key]: n,
      });
    },
    previewCost(modeId) {
      return costPerOccurrence(modeId, this.localEconomics);
    },
    formatCurrency(v) {
      if (!Number.isFinite(v)) return '$0';
      return `$${Math.round(v).toLocaleString()}`;
    },
  },
};
</script>

<style scoped>
.economics-form {
  padding: 4px 4px 12px;
}
.field-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.field-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.field-label {
  font-weight: 500;
  margin: 0;
}
.field-unit {
  margin: 0;
  font-weight: 500;
}
.field-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}
.field-slider {
  flex: 1;
  margin: 0;
  min-width: 0;
}
.field-number {
  width: 96px;
  flex-shrink: 0;
}
@media (max-width: 480px) {
  .field-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
  .field-number {
    width: 100%;
  }
}
.preview-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}
.preview-cost {
  margin-left: 6px;
  font-weight: 600;
}
</style>
